import React, { useEffect, useRef, useState } from 'react';
import * as d3 from 'd3';
import { feature } from 'topojson-client';
import { Grid, IconButton } from '@mui/material';
import Tooltip from '../tooltip';
import MunicipioModal from './modalAssistencia';
import municipiosData from '../municipios.json';
import PainelAssistencia from './painelAssistencia';
import AddIcon from '@mui/icons-material/Add';
import RemoveIcon from '@mui/icons-material/Remove';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import { useDevice } from 'components/DeviceContext.js';
import { getColorByFilterAndResponse, getColorByResponse } from 'services/colorService';

const MapaAssistencia = () => {
	const { isMobile } = useDevice();
	const svgRef = useRef();
	const zoomRef = useRef();
	const [tooltipPos, setTooltipPos] = useState({ x: 0, y: 0 });
	const [tooltipContent, setTooltipContent] = useState('');
	const [selectedUF, setSelectedUF] = useState(null);
	const [selectedMunicipio, setSelectedMunicipio] = useState(null);
	const [isModalOpen, setIsModalOpen] = useState(false);
	const [modalData, setModalData] = useState(null);
	const [locationIconCoords, setLocationIconCoords] = useState(null);
	const [currentColor, setCurrentColor] = useState(null);
	const [legendData, setLegendData] = useState([]);
	const [isFilterExpanded, setIsFilterExpanded] = useState(true);
	const [selectedRede, setSelectedRede] = useState(null);
	const [transform, setTransform] = useState(d3.zoomIdentity);

	useEffect(() => {
		const fetchData = async () => {
			try {
				const response = await fetch(`${process.env.REACT_APP_API_URL}/mapa-assistencia`);
				const data = await response.json();

				const filteredData = selectedRede ? data.filter(item => item.rede === selectedRede) : data;
				setLegendData(data);

				const geojsonData = feature(municipiosData, municipiosData.objects.municipios);
				const filteredGeojsonData = selectedUF ? geojsonData.features.filter(municipios => municipios.properties.uf === selectedUF) : geojsonData.features;

				const width = svgRef.current.clientWidth;
				const height = svgRef.current.clientHeight;

				const svg = d3.select(svgRef.current)
					.attr('viewBox', `0 0 ${width} ${height}`)
					.attr('preserveAspectRatio', 'xMidYMid meet');

				svg.selectAll('*').remove();

				const g = svg.append('g');

				const projection = d3.geoMercator().fitSize([width, height], geojsonData);
				const path = d3.geoPath().projection(projection);

				g.selectAll('path')
					.data(filteredGeojsonData)
					.enter()
					.append('path')
					.attr('d', path)
					.style('fill', d => {
						const ibge = Number(d.properties.ibge);
						const municipio = filteredData.find(item => item.ibge === ibge);

						// Verifica se existe um município e uma resposta
						const resposta = municipio ? municipio.resposta : null;
						const rede = municipio ? municipio.rede : null;  // Adiciona a rede ao município

						// Passa tanto a rede quanto a resposta para a função
						const color = (rede && resposta)
							? getColorByFilterAndResponse(rede, resposta)
							: 'var(--sem-rede)';  // Usa uma cor padrão caso não tenha rede ou resposta

						return color;
					})
					.style('stroke', '#fff')
					.style('stroke-width', '0.2px')
					.on('mouseover', (event, d) => {
						const [x, y] = d3.pointer(event);
						setTooltipPos({ x, y });
						setTooltipContent(d.properties.municipio);
						const ibge = Number(d.properties.ibge);
						const municipio = filteredData.find(item => item.ibge === ibge);
						const color = municipio ? municipio.cor_resposta : null;
						setCurrentColor(color);
					})
					.on('mouseout', () => {
						setTooltipContent('');
					})
					.on('click', (event, d) => {
						const { nome, ibge } = d.properties;
						setModalData({ nome, ibge });
						setIsModalOpen(true);

						const [x, y] = path.centroid(d);
						setLocationIconCoords({ x, y });
					});

				if (selectedUF) {
					const ufData = geojsonData.features.filter(municipios => municipios.properties.uf === selectedUF);
					const bounds = path.bounds({ type: 'FeatureCollection', features: ufData });
					const dx = bounds[1][0] - bounds[0][0];
					const dy = bounds[1][1] - bounds[0][1];
					const x = (bounds[0][0] + bounds[1][0]) / 2;
					const y = (bounds[0][1] + bounds[1][1]) / 2;
					const scale = Math.max(1, Math.min(8, 0.9 / Math.max(dx / width, dy / height)));
					const translate = [width / 2 - scale * x, height / 2 - scale * y];

					svg.transition()
						.duration(750)
						.call(
							zoomRef.current.transform,
							d3.zoomIdentity.translate(translate[0], translate[1]).scale(scale)
						);
				}

				const zoom = d3.zoom()
					.scaleExtent([1, 8])
					.on('zoom', (event) => {
						g.attr('transform', event.transform);
						setTransform(event.transform);
					});

				svg.call(zoom);
				zoomRef.current = zoom;

			} catch (error) {
				console.error('Erro ao carregar os dados:', error);
			}
		};

		fetchData();
	}, [selectedUF, selectedMunicipio, selectedRede]);

	const closeModal = () => {
		setIsModalOpen(false);
		setModalData(null);
	};

	const handleFilterChange = (selectedRede) => {
		setSelectedRede(selectedRede);
		setSelectedUF(null);
		setSelectedMunicipio(null);
	};

	const handleMunicipioSelect = (selectedMunicipio) => {
		setSelectedMunicipio(selectedMunicipio);
	};

	const handleZoomIn = () => {
		const svg = d3.select(svgRef.current);
		svg.transition().call(zoomRef.current.scaleBy, 1.3);
	};

	const handleZoomOut = () => {
		const svg = d3.select(svgRef.current);
		svg.transition().call(zoomRef.current.scaleBy, 1 / 1.3);
	};

	return (
		<Grid container style={{ display: 'flex', justifyContent: 'center', backgroundColor: 'var(--azul)', paddingBottom: isMobile ? '0' : '1em' }}>
			<Grid item lg={12} style={{ display: 'flex', justifyContent: 'center', paddingBottom: '0.6em', backgroundColor: 'rgba(83, 108, 149, 0.5)', borderRadius: '0 0 50px 50px', marginBottom: '1em', flexWrap: 'wrap' }}>
				<Grid item lg={2}>
					<p>MAPA DA ASSISTÊNCIA</p>
				</Grid>
				<Grid item lg={6}>
					<p style={{ fontSize: '14px', textAlign: 'center' }}>Obtenha informações detalhadas sobre a estrutura da rede de assistência ao usuário de drogas nos Municípios brasileiros e saiba onde procurar ajuda.</p>
				</Grid>
			</Grid>
			<Grid item xs={12} md={2} style={{ marginTop: '16px', width: isFilterExpanded ? '12vw' : 'auto' }}>
				<PainelAssistencia
					legendData={legendData}
					selectedRede={selectedRede}
					selectedUF={selectedUF}
					currentColor={currentColor}
					onFilterChange={handleFilterChange}
					onSelectMunicipio={handleMunicipioSelect}
				/>
			</Grid>
			<Grid item xs={12} md={10} lg={5} style={{ position: 'relative', minHeight: '62vh' }}>
				<svg ref={svgRef} style={{ width: '100%', height: '100%' }}></svg>
				<div style={{ position: 'absolute', top: 10, right: 10, display: 'flex', flexDirection: 'column' }}>
					<IconButton onClick={handleZoomIn}>
						<AddIcon />
					</IconButton>
					<IconButton onClick={handleZoomOut}>
						<RemoveIcon />
					</IconButton>
				</div>
				{locationIconCoords && (
					<div
						style={{
							position: 'absolute',
							left: transform.applyX(locationIconCoords.x) - 9,
							top: transform.applyY(locationIconCoords.y) - 7,
							transformOrigin: 'center'
						}}
					>
						<LocationOnIcon style={{ fontSize: '20px', color: 'red' }} />
					</div>
				)}
				{tooltipContent && <Tooltip x={tooltipPos.x} y={tooltipPos.y} content={tooltipContent} />}
				{isModalOpen && modalData && <MunicipioModal municipioData={modalData} onClose={closeModal} />}
			</Grid>
		</Grid>
	);
};

export default MapaAssistencia;
